import { cn } from "@/lib/utils";
import { Bookmark, ChevronRight, Heart, MessageCircle, Send } from "lucide-react";
import type { CampaignExample } from "../../../../data";
import { campaignAccentClasses } from "../../campaignAccent.util";

interface AdMockProps {
  campaign: CampaignExample;
  sponsored: string;
}

// The campaign as a sponsored social post: square artwork, a call-to-action
// strip, the action icons and a caption.
const AdMock = ({ campaign, sponsored }: AdMockProps) => {
  const accent = campaignAccentClasses[campaign.accent];
  return (
    <div className="w-full max-w-[272px] overflow-hidden rounded-xl border border-line bg-white text-[13px] text-ink shadow-card">
      <div className="flex items-center gap-2 px-2.5 py-2">
        <span
          aria-hidden="true"
          className={cn(
            "grid size-[30px] shrink-0 place-items-center rounded-full text-[12px] font-extrabold text-white",
            accent.solid,
          )}
        >
          {campaign.brand.initial}
        </span>
        <div className="min-w-0">
          <b className="block text-[12.5px]">{campaign.brand.handle}</b>
          <small className="block text-[11px] text-ink-muted">{sponsored}</small>
        </div>
        <span aria-hidden="true" className="ml-auto font-extrabold tracking-[2px]">
          ···
        </span>
      </div>
      <div
        className={cn(
          "relative grid aspect-square content-center justify-items-start gap-1 overflow-hidden p-[18px]",
          accent.art,
        )}
      >
        <i aria-hidden="true" className="absolute -top-[30px] -right-10 size-[150px] rounded-full bg-white/30" />
        <i aria-hidden="true" className="absolute right-[30px] -bottom-[30px] size-[90px] rounded-full bg-white/20" />
        <small className="relative text-[11px] font-extrabold tracking-[0.08em] uppercase">
          {campaign.ad.kicker}
        </small>
        <b className="relative text-[2.1rem] leading-none font-extrabold tracking-[-0.03em]">
          {campaign.offer.value}
        </b>
        <span className="relative max-w-[18ch] text-[13px] font-semibold">
          {campaign.offer.text}
        </span>
      </div>
      <div className="flex items-center justify-between bg-tone-sky px-3 py-[9px] font-bold text-tone-sky-ink">
        <span>{campaign.ad.cta}</span>
        <ChevronRight aria-hidden="true" className="size-4" />
      </div>
      <div aria-hidden="true" className="flex items-center gap-3 px-2.5 pt-2 pb-0.5">
        <Heart className="size-5" strokeWidth={1.8} />
        <MessageCircle className="size-5" strokeWidth={1.8} />
        <Send className="size-5" strokeWidth={1.8} />
        <span className="flex-1" />
        <Bookmark className="size-5" strokeWidth={1.8} />
      </div>
      <p className="px-2.5 pt-0.5 pb-2.5 text-[12px] leading-[1.4]">
        <b>{campaign.brand.handle}</b> {campaign.ad.caption}
      </p>
    </div>
  );
};

export default AdMock;
